// Calendar range presets for the Reports toolbar.
//
// Kept apart from the page because this is the part that can actually be
// tested: nothing in the reports stack executes SQL, so month-end lengths,
// quarter boundaries and the January rollover are the only logic here with a
// real chance of being wrong, and they get tests.
//
// Local time throughout, matching the epoch-ms the API stores and the
// DateInput on this page. The home-timezone system setting is server-side and
// deliberately not involved.

// Day boundaries now live in utils/date.ts, so this module and calendarRange.ts
// share one definition. Re-exported because callers have always imported
// endOfDay from here.
import { endOfDay } from './date';

export { endOfDay };

export type RangePresetKey =
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'this-year'
  | 'last-year'
  | 'all-time'
  | 'custom';

export interface ReportRange {
  since: number;
  until: number;
}

export const RANGE_PRESETS: { key: RangePresetKey; label: string }[] = [
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'this-quarter', label: 'This quarter' },
  { key: 'this-year', label: 'This year' },
  { key: 'last-year', label: 'Last year' },
  { key: 'all-time', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

/** Local midnight on the first of month `m` of year `y`. */
function startOf(y: number, m: number): number {
  return new Date(y, m, 1, 0, 0, 0, 0).getTime();
}

/**
 * Local 23:59:59.999 on the last day of month `m`.
 *
 * Day 0 of the NEXT month is the last day of this one, which is what avoids
 * hardcoding 28/30/31 and gets February right in a leap year for free.
 */
function endOf(y: number, m: number): number {
  return new Date(y, m + 1, 0, 23, 59, 59, 999).getTime();
}

/**
 * Resolve a preset to an absolute range.
 *
 * A preset covers its WHOLE calendar period rather than stopping at today —
 * "This year" is 01/01 to 12/31. Data cannot exist in the future, so the extra
 * span selects nothing, and it keeps "This month" the same shape as "Last
 * month" instead of one being silently truncated.
 *
 * `custom` has no answer of its own; the caller keeps whatever the user typed.
 * Callers must not pass it — resolveRange() below is the one that handles it.
 */
export function resolvePreset(key: Exclude<RangePresetKey, 'custom'>, now: number): ReportRange {
  const d = new Date(now);
  const y = d.getFullYear();
  const m = d.getMonth();

  switch (key) {
    case 'this-month':
      return { since: startOf(y, m), until: endOf(y, m) };
    // `m - 1` is -1 in January; the Date constructor normalises that to
    // December of the previous year, so the rollover needs no special case.
    case 'last-month':
      return { since: startOf(y, m - 1), until: endOf(y, m - 1) };
    case 'this-quarter': {
      const q = Math.floor(m / 3) * 3;
      return { since: startOf(y, q), until: endOf(y, q + 2) };
    }
    case 'this-year':
      return { since: startOf(y, 0), until: endOf(y, 11) };
    case 'last-year':
      return { since: startOf(y - 1, 0), until: endOf(y - 1, 11) };
    // 0, not "omit the param": the API defaults a missing `since` to 90 days
    // ago, so leaving it out asks for a quarter rather than everything.
    case 'all-time':
      return { since: 0, until: endOf(y, 11) };
  }
}

/**
 * The range a toolbar in state `key` should request.
 *
 * Custom falls back to the current month for whichever bound the user has left
 * empty, so a half-filled custom range still asks a sensible question.
 */
export function resolveRange(
  key: RangePresetKey,
  custom: { since: number | null; until: number | null },
  now: number,
): ReportRange {
  if (key !== 'custom') return resolvePreset(key, now);

  const fallback = resolvePreset('this-month', now);
  return {
    since: custom.since ?? fallback.since,
    // DateInput emits local midnight; widen it so a same-day range covers the
    // whole day rather than a single instant.
    until: custom.until !== null ? endOfDay(custom.until) : fallback.until,
  };
}
